import { ExportOutlined } from "@ant-design/icons";
import { ProTable } from "@ant-design/pro-components";
import type { ProColumns } from "@ant-design/pro-components";
import { Button, Tag } from "antd";
import { Link } from "@tanstack/react-router";
import {
  listGuides,
  STAGE_LABELS,
  STATUS_COLORS,
  STATUS_LABELS,
  type GuideListItem,
  type GuideStage,
  type GuideStatus,
} from "@/features/guides/api";

// 管理端指南列表:搜索/筛选/分页 + 跳门户详情页办理状态机操作(本页不做流转)。

function toArray<T>(v: T | T[] | undefined): T[] | undefined {
  if (v === undefined || v === null) return undefined;
  return Array.isArray(v) ? v : [v];
}

const STATUS_VALUE_ENUM = Object.fromEntries(
  Object.entries(STATUS_LABELS).map(([k, v]) => [k, { text: v }]),
) as Record<GuideStatus, { text: string }>;
const STAGE_VALUE_ENUM = Object.fromEntries(
  Object.entries(STAGE_LABELS).map(([k, v]) => [k, { text: v }]),
) as Record<GuideStage, { text: string }>;

const COLUMNS: ProColumns<GuideListItem>[] = [
  { title: "注册号", dataIndex: "reg_no", width: 150, copyable: true, search: false },
  { title: "标题", dataIndex: "title", ellipsis: true },
  {
    title: "状态",
    dataIndex: "status",
    width: 110,
    valueType: "select",
    valueEnum: STATUS_VALUE_ENUM,
    fieldProps: { mode: "multiple", allowClear: true },
    render: (_, r) => <Tag color={STATUS_COLORS[r.status]}>{STATUS_LABELS[r.status]}</Tag>,
  },
  {
    title: "阶段",
    dataIndex: "stage",
    width: 100,
    valueType: "select",
    valueEnum: STAGE_VALUE_ENUM,
    fieldProps: { mode: "multiple", allowClear: true },
    render: (_, r) => <Tag>{STAGE_LABELS[r.stage]}</Tag>,
  },
  {
    title: "分数",
    dataIndex: "score",
    width: 80,
    align: "center",
    search: false,
    sorter: true,
    render: (_, r) => r.score ?? "-",
  },
  {
    title: "等级",
    dataIndex: "level",
    width: 80,
    align: "center",
    search: false,
    render: (_, r) => (r.level ? <Tag color="gold">{r.level}</Tag> : "-"),
  },
  { title: "发布机构", dataIndex: "release_org", ellipsis: true, search: false },
  {
    title: "创建时间",
    dataIndex: "created_at",
    width: 160,
    search: false,
    sorter: true,
    render: (_, r) => new Date(r.created_at).toLocaleString("zh-CN", { hour12: false }),
  },
  {
    title: "操作",
    valueType: "option",
    width: 90,
    render: (_, r) => [
      <Button
        key="view"
        size="small"
        type="link"
        icon={<ExportOutlined />}
        onClick={() => window.open(`/inx/guide/${r.id}`, "_blank")}
      >
        查看
      </Button>,
    ],
  },
];

export function GuidesManagePage() {
  return (
    <ProTable<GuideListItem>
      headerTitle="指南管理"
      rowKey="id"
      columns={COLUMNS}
      search={{ labelWidth: 60 }}
      pagination={{ defaultPageSize: 20 }}
      toolBarRender={() => [
        <Link key="new" to="/admin/guides/new">
          <Button type="primary">新建指南</Button>
        </Link>,
      ]}
      request={async (params, sort) => {
        const { current = 1, pageSize = 20, title, status, stage } = params;
        // ProTable 排序只开放 created_at/score 两列,与后端 sort 白名单一致
        let sortField: "created_at" | "score" = "created_at";
        let order: "asc" | "desc" = "desc";
        for (const key of ["created_at", "score"] as const) {
          const s = sort?.[key];
          if (s) {
            sortField = key;
            order = s === "ascend" ? "asc" : "desc";
            break;
          }
        }
        const out = await listGuides({
          page: current,
          page_size: pageSize,
          q: title as string | undefined,
          status: toArray(status as GuideStatus | GuideStatus[] | undefined),
          stage: toArray(stage as GuideStage | GuideStage[] | undefined),
          sort: sortField,
          order,
        });
        return { data: out.items, total: out.total, success: true };
      }}
    />
  );
}
